import { useEffect, useMemo, useRef, useState } from 'react';
import getConfigFile from 'configFiles';
import { initializeApp } from 'firebase/app';
import { FacebookAuthProvider, getAuth, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { auth } from 'firebaseui';

const firebaseAuthPromise = getConfigFile('firebase')
  .then((it) => initializeApp(it))
  .then((it) => getAuth(it));

export default function FirebaseUIAuth({ uiCallback, fbSignIn }: {
  uiCallback?: (it: auth.AuthUI) => void;
  fbSignIn: (it: any) => boolean;
}) {
  const [firebaseui, setFirebaseui] = useState(null);
  const [userSignedIn, setUserSignedIn] = useState(false);
  const [firebaseAuth, setFirebaseAuth] = useState(null);

  useEffect(() => {
    firebaseAuthPromise.then(setFirebaseAuth);
  }, []);

  // Configure FirebaseUI.
  const uiConfig = useMemo(
    () => ({
      // Popup signin flow rather than redirect flow.
      signInFlow: 'popup',
      // We will display Google and Facebook as auth providers.
      signInOptions: [
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          scopes: ['email'],
        },
        {
          provider: FacebookAuthProvider.PROVIDER_ID,
          scopes: ['email'],
        },
        {
          provider: 'apple.com',
          scopes: ['email'],
        },
      ],
      callbacks: {
        // Avoid redirects after sign-in.
        signInSuccessWithAuthResult: fbSignIn,
      },
    }),
    [fbSignIn],
  );

  const elementRef = useRef(null);
  useEffect(() => {
    // Firebase UI only works on the Client. So we're loading the package only after
    // the component has mounted, so that this works when doing server-side rendering.
    import('firebaseui').then((mod) => {
      setFirebaseui(mod.default || mod);
    });
  }, []);

  useEffect(() => {
    if (firebaseui === null || firebaseAuth === null) {
      return;
    }

    // Get or Create a firebaseUI instance.
    const firebaseUiWidget: auth.AuthUI =
      firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(firebaseAuth);

    if (uiConfig.signInFlow === 'popup') {
      firebaseUiWidget.reset();
    }

    // We track the auth state to reset firebaseUi if the user signs out.
    const unregisterAuthObserver = onAuthStateChanged(firebaseAuth, (user) => {
      if (!user && userSignedIn) {
        firebaseUiWidget.reset();
      }
      setUserSignedIn(!!user);
    });

    // Trigger the callback if any was set.
    if (uiCallback) {
      uiCallback(firebaseUiWidget);
    }

    // Render the firebaseUi Widget.
    firebaseUiWidget.start(elementRef.current, uiConfig);

    unregisterAuthObserver();
    firebaseUiWidget.reset();
  }, [firebaseui, userSignedIn, uiCallback, uiConfig]);

  useEffect(() => {
    if (firebaseui !== null) {
      // Import the css only on the client.
      import('firebaseui/dist/firebaseui.css');
    }
  }, [firebaseui]);

  return <div ref={elementRef} />;
}
